import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Divider,
  Chip,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import ResultCard from '../components/ResultCard';
import LoadingAnimation from '../components/LoadingAnimation';
import { searchUsers } from '../services/api';

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useState({});
  const [filterText, setFilterText] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);
  const [executionTime, setExecutionTime] = useState(null);

  // Parse query params from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryParams = {};
    
    for (const [key, value] of params.entries()) {
      queryParams[key] = value;
    }
    
    setSearchParams(queryParams);
    console.log('Parsed search params:', queryParams);
    
    // Only fetch if we have search parameters
    if (Object.keys(queryParams).length > 0) {
      fetchResults(queryParams);
    } else {
      setLoading(false);
      setError('No search parameters provided. Please enter at least one search term.');
    }
  }, [location.search]);

  // Filter results when filterText changes
  useEffect(() => {
    if (!filterText.trim()) {
      setFilteredResults(results);
      return;
    }
    
    const filtered = results.filter(user => {
      const searchableText = JSON.stringify(user).toLowerCase();
      return searchableText.includes(filterText.toLowerCase());
    });
    
    setFilteredResults(filtered);
  }, [filterText, results]);

  const fetchResults = async (params) => {
    setLoading(true);
    setError(null);
    setExecutionTime(null);
    
    try {
      console.log('Fetching results with params:', params);
      const response = await searchUsers(params);
      
      console.log('Search response:', response);
      setResults(response.data || []);
      setFilteredResults(response.data || []);
      
      // Set execution time if available
      if (response.execution_time_ms) {
        setExecutionTime(response.execution_time_ms);
      }
      
      // Log counts for debugging
      console.log(`Retrieved ${response.data?.length || 0} results`);
    } catch (err) {
      console.error('Error fetching results:', err);
      
      // Provide more descriptive error message
      let errorMessage = 'Failed to fetch results. Please try again.';
      
      if (err.response) {
        errorMessage = `Server error (${err.response.status}): ${err.response.data?.message || 'Unknown error'}`;
      } else if (err.request) {
        errorMessage = 'Network error: Could not connect to the server. Please check your connection.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/');
  };

  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
  };

  const clearFilter = () => {
    setFilterText('');
  };

  // Create a summary of search parameters
  const renderSearchSummary = () => {
    return Object.entries(searchParams).map(([key, value]) => (
      <Chip
        key={key}
        label={`${key}: ${value}`}
        sx={{ m: 0.5 }}
        color="primary"
        variant="outlined"
      />
    ));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #121212 0%, #1E1E1E 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 2,
              background: 'rgba(30, 30, 30, 0.9)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton onClick={handleBackClick} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
                Search Results
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }}>
              {renderSearchSummary()}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              mb: 2
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 2 
              }}>
                <Typography variant="body1" color="text.secondary">
                  Found {filteredResults.length} {filteredResults.length === 1 ? 'result' : 'results'}
                  {filterText && ` matching "${filterText}"`}
                  {executionTime && ` in ${executionTime}ms`}
                </Typography>
                
                {executionTime && executionTime < 500 && (
                  <Chip 
                    label={`Ultra-fast (${executionTime}ms)`} 
                    color="success" 
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
              
              <Box sx={{ 
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', md: 'row' }
              }}>
                <TextField
                  placeholder="Filter results..."
                  value={filterText}
                  onChange={handleFilterChange}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon />
                      </InputAdornment>
                    ),
                    endAdornment: filterText && (
                      <InputAdornment position="end">
                        <IconButton onClick={clearFilter} edge="end" size="small">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    minWidth: { md: '250px' },
                    flexGrow: { xs: 1, md: 0 }
                  }}
                />
                
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleBackClick}
                  sx={{ 
                    whiteSpace: 'nowrap',
                    minWidth: { xs: '100%', md: 'auto' }
                  }}
                >
                  New Search
                </Button>
              </Box>
            </Box>
          </Paper>
        </motion.div>

        {loading ? (
          <LoadingAnimation text="Searching databases..." />
        ) : error ? (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        ) : filteredResults.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 2,
                background: 'rgba(30, 30, 30, 0.9)',
              }}
            >
              <Typography variant="h6" gutterBottom>
                No results found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Try adjusting your search criteria or try a different search term.
              </Typography>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleBackClick}
                sx={{ mt: 3 }}
              >
                New Search
              </Button>
            </Paper>
          </motion.div>
        ) : (
          <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Found {filteredResults.length} {filteredResults.length === 1 ? 'result' : 'results'}
                {filterText && ` matching "${filterText}"`}
                {executionTime && ` in ${executionTime}ms`}
              </Typography>
              
              {executionTime && executionTime < 500 && (
                <Chip 
                  label={`Ultra-fast (${executionTime}ms)`} 
                  color="success" 
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
            </Box>
            
            <Grid container spacing={3}>
              {filteredResults.map((user, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={user._id}>
                  <ResultCard user={user} index={index} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Container>
    </Box>
  );
};

export default ResultsPage; 